<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { type EChartsType } from 'echarts'
import * as echarts from 'echarts'
import type { KpiResponse } from '@/api-swagger'
import { useKpiStore } from '@/stores/useKpiStore'
import { storeToRefs } from 'pinia'
import LoaderCenter from '@/components/common/feedback/LoaderCenter.vue'

const kpiStore = useKpiStore()

const { startDate, endDate, viewType, categoryColors, usersSelected } = storeToRefs(kpiStore)

interface Props {
  data: KpiResponse[]
  title?: string
  loading?: boolean
  error?: string | null
}
type SerieType = {
  name: string
  type: string
  data: {
    value: number
    sum: string | number | undefined
    label: {
      show: boolean
      position: string
    }
  }[]
  color: string
  emphasis: {
    focus: string
  }
  stack: string
}

type TrendLineType = {
  name: string
  type: string
  data: (number | null)[]
  color: string
  lineStyle: {
    type: string
    width: number
    color: string
  }
  symbol: string
  connectNulls: boolean
  showSymbol: boolean
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  loading: false,
  error: null,
})

const chartRef = ref<HTMLElement | null>(null)
let chartInstance: EChartsType | null = null

const hashString = (str: string): number => {
  let hash = 0
  if (str.length === 0) return hash
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash
  }
  return Math.abs(hash)
}

const getColorFromId = (userId: number): string => {
  const startColor = {
    r: parseInt('2b', 16),
    g: parseInt('58', 16),
    b: parseInt('76', 16),
  }

  const endColor = {
    r: parseInt('00', 16),
    g: parseInt('d6', 16),
    b: parseInt('bf', 16),
  }

  const hash = hashString(userId.toString())
  const position = (hash % 100) / 100

  const r = Math.round(startColor.r + position * (endColor.r - startColor.r))
  const g = Math.round(startColor.g + position * (endColor.g - startColor.g))
  const b = Math.round(startColor.b + position * (endColor.b - startColor.b))

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
}

const getColorFromCategory = (category?: string, color?: string): string => {
  if (category) {
    return categoryColors.value[`${category}`] || '#95A5A6'
  }

  if (color) {
    const entry = Object.entries(categoryColors.value).find(([_, value]) => value === color)
    return entry ? entry[0] : ''
  }

  return '#95A5A6'
}

const xAxisData = computed(() => {
  if (!startDate.value || !endDate.value) return []

  const start = new Date(startDate.value)
  const end = new Date(endDate.value)
  const labels: string[] = []

  switch (viewType.value) {
    case 'day': {
      const current = new Date(start)
      while (current <= end) {
        labels.push(
          `${current.getDate().toString().padStart(2, '0')}/${(current.getMonth() + 1).toString().padStart(2, '0')}`,
        )
        current.setDate(current.getDate() + 1)
      }
      break
    }
    case 'week': {
      const current = new Date(start)
      let weekNum = 1
      while (current <= end) {
        labels.push(`S${weekNum}`)
        current.setDate(current.getDate() + 7)
        weekNum++
      }
      break
    }
    case 'month': {
      const current = new Date(start)
      const monthNames = [
        'Janvier',
        'Février',
        'Mars',
        'Avril',
        'Mai',
        'Juin',
        'Juillet',
        'Août',
        'Septembre',
        'Octobre',
        'Novembre',
        'Décembre',
      ]

      while (current <= end) {
        labels.push(monthNames[current.getMonth()])
        current.setMonth(current.getMonth() + 1)
      }
      break
    }
    case 'year': {
      const current = new Date(start)
      while (current <= end) {
        labels.push(current.getFullYear().toString())
        current.setFullYear(current.getFullYear() + 1)
      }
      break
    }
  }
  return labels
})

const calculateRegressionLine = (dataPoints: number[]): (number | null)[] => {
  const n = dataPoints.length
  const sumX = dataPoints.reduce((sum, _, index) => sum + index, 0)
  const sumY = dataPoints.reduce((sum, value) => sum + value, 0)
  const sumXY = dataPoints.reduce((sum, value, index) => sum + index * value, 0)
  const sumXX = dataPoints.reduce((sum, _, index) => sum + index * index, 0)

  const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX)
  const intercept = (sumY - slope * sumX) / n

  const trendData: (number | null)[] = []
  for (let i = 0; i < dataPoints.length; i++) {
    trendData.push(slope * i + intercept)
  }
  return trendData
}

const groupDataByViewType = (data: object): {count: number, sum: number | undefined}[] => {
  if (!startDate.value || !endDate.value) return []

  const start = new Date(startDate.value)
  start.setHours(0, 0, 0, 0)
  const end = new Date(endDate.value)
  end.setHours(23, 59, 59, 999)
  const groupedData: {count: number, sum: number | undefined}[] = []

  const filteredData = Object.entries(data)
    .filter(([dateKey]) => {
      const date = new Date(dateKey)
      return date >= start && date <= end
    })
    .sort(([a], [b]) => a.localeCompare(b))

  const hasFinancialData = filteredData.some(([, value]) => typeof value === 'object' && value !== null)

  switch (viewType.value) {
    case 'day': {
      const current = new Date(start)
      while (current <= end) {
        const dateKey = current.toISOString().split('T')[0]
        const entry = filteredData.find(([key]) => key === dateKey)
        groupedData.push({
          count: entry ? ((entry[1].count || entry[1]) as number) : 0,
          sum: entry && hasFinancialData
            ? (entry[1].margin || (entry[1].expected_revenue + entry[1].recurring_revenue) || 0).toFixed(2) as unknown as number
            : undefined,
        })
        current.setDate(current.getDate() + 1)
      }
      break
    }
    case 'week': {
      const current = new Date(start)
      while (current <= end) {
        let weekSum = 0
        let weekMarge = 0
        const weekEnd = new Date(current)
        weekEnd.setDate(weekEnd.getDate() + 6)

        filteredData.forEach(([dateKey, value]) => {
          const date = new Date(dateKey)
          if (date >= current && date <= weekEnd && date <= end) {
            weekSum += (value.count || value) as number
            if (hasFinancialData) {
              weekMarge += (value.margin || (value.expected_revenue + value.recurring_revenue) || 0) as number
            }
          }
        })
        groupedData.push({
          count: weekSum,
          sum: hasFinancialData ? weekMarge.toFixed(2) as unknown as number : undefined,
        })

        current.setDate(current.getDate() + 7)
      }
      break
    }
    case 'month': {
      const current = new Date(start)
      while (current <= end) {
        let monthSum = 0
        let monthMarge = 0
        const monthStart = new Date(current.getFullYear(), current.getMonth(), 1)
        const monthEnd = new Date(current.getFullYear(), current.getMonth() + 1, 0)

        filteredData.forEach(([dateKey, value]) => {
          const date = new Date(dateKey)
          if (date >= monthStart && date <= monthEnd) {
            monthSum += (value.count || value) as number
            if (hasFinancialData) {
              monthMarge += (value.margin || (value.expected_revenue + value.recurring_revenue) || 0) as number
            }
          }
        })
        groupedData.push({
          count: monthSum,
          sum: hasFinancialData ? monthMarge.toFixed(2) as unknown as number : undefined,
        })

        current.setMonth(current.getMonth() + 1)
      }
      break
    }
    case 'year': {
      const current = new Date(start)
      while (current <= end) {
        let yearSum = 0
        let yearMarge = 0
        const yearStart = new Date(current.getFullYear(), 0, 1)
        const yearEnd = new Date(current.getFullYear(), 11, 31)

        filteredData.forEach(([dateKey, value]) => {
          const date = new Date(dateKey)
          if (date >= yearStart && date <= yearEnd) {
            yearSum += (value.count || value) as number
            if (hasFinancialData) {
              yearMarge += (value.margin || (value.expected_revenue + value.recurring_revenue) || 0) as number
            }
          }
        })
        groupedData.push({
          count: yearSum,
          sum: hasFinancialData ? yearMarge.toFixed(2) as unknown as number : undefined,
        })

        current.setFullYear(current.getFullYear() + 1)
      }
      break
    }
  }
  return groupedData
}

const prepareChartData = computed(() => {
  // sort props.data by userName alphabetically
  const sortData = [...props.data].sort((a, b) => a.userName.localeCompare(b.userName, 'fr-FR'))
  const series = sortData?.map((item: KpiResponse) => {
    if (Array.isArray(item.data)) {
      const tabs: Record<string, Record<string, number>> = {
        R2P: {},
        R1P: {},
        R2C: {},
        R1C: {},
        'R+P': {},
        'R+C': {},
        Autres: {},
      }
      item.data.forEach((obj: { day: string; categories: Record<string, number> }) => {
        const key = obj.day
        Object.keys(obj.categories).forEach((keyCat: string) => {
          const tabsKey = keyCat in tabs ? keyCat : 'Autres'

          tabs[tabsKey][key] = obj.categories[keyCat]
        })
      })
      const result: SerieType[] = []
      Object.keys(tabs).forEach((key: string) => {
        const groupedData = groupDataByViewType(tabs[key])
        result.push({
          name: item.userName,
          type: 'bar',
          data: groupedData.map((value) => ({
            value: value.count,
            sum: value.sum,
            label: {
              show: false,
              position: 'inside',
            },
          })),
          color: getColorFromCategory(key),
          emphasis: {
            focus: 'series',
          },
          stack: item.userName,
        })
      })
      return result
    }
    const groupedData = groupDataByViewType(item.data)
    return {
      name: item.userName,
      type: 'bar',
      data: groupedData.map((value: any) => {
        return {
          value: value.count || value,
          sum: value.sum,
          label: {
            show: (value.count || value) > 0,
            position: 'top',
          },
        }
      }),
      color: getColorFromId(item.userId),
      emphasis: {
        focus: 'series',
      },
      stack: item.userName,
    }
  })

  const flatSeries = series.flat() as (SerieType | TrendLineType)[]

  // Ajouter la ligne de tendance uniquement si 1 utilisateur est sélectionné
  if (usersSelected?.value?.length === 1) {
    const userId = usersSelected.value[0]

    // Calculer les totaux par période pour l'utilisateur sélectionné
    const selectedUserData = sortData.find((item) => item.userId === userId)
    if (selectedUserData) {
      let dataPoints: number[]

      if (Array.isArray(selectedUserData.data)) {
        // Si les données sont en catégories, sommer toutes les catégories par période
        const tabs: Record<string, Record<string, number>> = {
          R2P: {},
          R1P: {},
          R2C: {},
          R1C: {},
          'R+P': {},
          'R+C': {},
          Autres: {},
        }
        selectedUserData.data.forEach(
          (obj: { day: string; categories: Record<string, number> }) => {
            const key = obj.day
            Object.keys(obj.categories).forEach((keyCat: string) => {
              const tabsKey = keyCat in tabs ? keyCat : 'Autres'
              tabs[tabsKey][key] = obj.categories[keyCat]
            })
          },
        )

        // Sommer toutes les catégories
        const allData: Record<string, number> = {}
        Object.values(tabs).forEach((categoryData) => {
          Object.entries(categoryData).forEach(([date, value]) => {
            allData[date] = (allData[date] || 0) + value
          })
        })

        dataPoints = groupDataByViewType(allData).map((value) => value.count)
      } else {
        dataPoints = groupDataByViewType(selectedUserData.data).map((value) => value.count)
      }

      // Calculer la ligne de tendance si on a au moins 2 points
      if (dataPoints.length >= 2) {
        const trendData = calculateRegressionLine(dataPoints)
        const trendLine: TrendLineType = {
          name: `Tendance`,
          type: 'line',
          color: '#2B3440FF',
          data: trendData,
          lineStyle: {
            type: 'dotted',
            width: 1,
            color: '#2B3440FF',
          },
          symbol: 'none',
          connectNulls: false,
          showSymbol: false,
        }

        flatSeries.push(trendLine)
      }
    }
  }

  return flatSeries
})

const initChart = () => {
  if (!chartRef.value) return

  const option = {
    xAxis: {
      type: 'category',
      data: xAxisData.value,
    },
    yAxis: {
      type: 'value',
    },
    tooltip: {
      trigger: 'item',
      formatter: (params: {
        seriesName: string
        name: string
        dataIndex: number
        color: string
      }) => {
        const t = params.seriesName
        let tooltip = `<p><strong>${params.name}</strong></p>
                      <p><strong>${params.seriesName}</strong></p>`

        const stackSeries = prepareChartData.value.filter((s) => {
          return s && 'stack' in s && s.stack && s.stack.includes(t)
        })
        let total = 0
        let marge = undefined

        for (const serie of stackSeries as SerieType[]) {
          const value = serie.data[params.dataIndex] || {
            value: 0,
            label: { show: false, position: 'inside' },
          }
          if (getColorFromCategory(undefined, serie.color)) {
            tooltip += `<div class="flex flex-row w-full justify-between align-items-center">
                <span class="badge badge-xs my-auto" style="background-color : ${serie.color};"></span>
                <p class="w-10 ms-5" style="font-weight: ${serie.color === params.color ? 'bolder' : 'normal'}" >${getColorFromCategory(undefined, serie.color)}</p>
                <span class="font-bold w-10 text-right">${value.value}</span>
                </div>`
          }
          if(value?.sum){
            if(!marge) marge = 0
            marge += parseFloat(value.sum as unknown as string) || 0
          }
          total += value.value
        }
        tooltip += `<div class="flex flex-row w-full justify-content-end align-items-center">
              <p class="w-1/2">Total</p>
              <strong class="w-1/2 self-end">${total}</strong>
            </div>`
        if(marge !== undefined)
          tooltip +=`
            <div class="flex flex-row w-full justify-content-end align-items-center">
              <p class="w-1/2">Marge</p>
              <strong class="w-1/2 self-end">${marge}€</strong>
            </div>`
        return tooltip
      },
      axisPointer: {
        type: 'shadow',
        label: {
          overflow: true,
        },
      },
      feature: {
        dataZoom: {
          yAxisIndex: 'none',
        },
      },
    },
    toolbox: {
      show: true,
      orient: 'vertical',
      top: '10%',
      feature: {
        saveAsImage: {
          show: true,
          name: props.title
            ? `${props.title} du ${new Date(startDate.value).toLocaleDateString('fr-FR')} au ${new Date(endDate.value).toLocaleDateString('fr-FR')}`
            : 'chart',
        },
        magicType: { show: true, type: ['line', 'bar'] },
      },
    },
    series: prepareChartData.value,
    legend: {
      width: 'auto',
      type: 'scroll',
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '15%',
      containLabel: true,
    },
  }

  if (!chartInstance) {
    chartInstance = echarts.init(chartRef.value)
  }
  // notMerge: true — replace all series/legend each time. Without this,
  // echarts merges by index and stale series (e.g. a removed commercial)
  // persist in the legend and the chart.
  chartInstance.setOption(option, true)
}

const handleResize = () => {
  chartInstance?.resize()
}

onMounted(() => {
  initChart()
  window.addEventListener('resize', handleResize)
})

watch(
  () => [props.data, xAxisData.value, viewType.value],
  () => {
    initChart()
  },
  { deep: true },
)

onBeforeUnmount(() => {
  if (chartInstance) {
    chartInstance.dispose()
  }
  window.removeEventListener('resize', handleResize)
})
</script>

<template>
  <div>
    <h3 v-if="title" class="text-center text-nowrap mb-4">{{ title }}</h3>
    <div class="relative w-full h-96">
      <div ref="chartRef" class="absolute inset-0 overflow-visible"></div>
      <div
        v-if="error"
        class="absolute inset-0 flex items-center justify-center bg-base-100 rounded-md"
      >
        <div class="alert alert-error max-w-md">
          <span>{{ error }}</span>
        </div>
      </div>
      <div
        v-else-if="loading"
        class="absolute inset-0 flex items-center justify-center bg-base-100 rounded-md"
      >
        <LoaderCenter />
      </div>
    </div>
  </div>
</template>

<style scoped></style>
